/*
 * Fallconi product section: gallery carousel, lookbook carousel and variant picker.
 * Used by `fallconi-main-product`. Re-initialises on theme editor section reloads.
 */
(() => {
  const INITIALISED = 'fallconiProductReady';

  const setupCarousel = (viewport, track, prev, next) => {
    if (!viewport || !track || !prev || !next) return;

    const step = () => Math.max(viewport.clientWidth * 0.92, 260);
      const lookbookSlides = viewport.hasAttribute('data-lookbook')
        ? Array.from(track.children)
        : [];

      const updateLookbookHeight = () => {
        if (!lookbookSlides.length) return;

        const index = Math.min(
          Math.round(track.scrollLeft / track.clientWidth),
          lookbookSlides.length - 1
        );
        const image = lookbookSlides[index]?.querySelector('img');
        if (image?.complete && image.getBoundingClientRect().height > 0) {
          viewport.style.height = `${image.getBoundingClientRect().height}px`;
        }
      };

    const updateArrows = () => {
      const { scrollLeft } = track;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const scrollable = maxScroll > 1;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft >= maxScroll - 1;

      [
        [prev, atStart],
        [next, atEnd],
      ].forEach(([button, disabled]) => {
        button.classList.toggle('is-disabled', disabled);
        /* Highlighted while images are still hidden on that side of the track. */
        button.classList.toggle('is-available', scrollable && !disabled);
        button.disabled = disabled;
      });
      updateLookbookHeight();
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

    track.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);

    /* Lazy-loaded images change the scrollable width after the first paint. */
    track.querySelectorAll('img').forEach((image) => {
      image.addEventListener('load', updateLookbookHeight);
    });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateArrows);
      observer.observe(track);
      Array.from(track.children).forEach((child) => observer.observe(child));
    }

    updateArrows();
    requestAnimationFrame(updateLookbookHeight);
  };

  const setupVariantPicker = (root) => {
    const buttons = root.querySelectorAll('[data-variant-id]');
    const variantInput = root.querySelector('[data-variant-id-input]');
    if (!buttons.length || !variantInput) return;

    const priceNode = root.querySelector('[data-product-price]');
    const comparePriceNode = root.querySelector('[data-product-compare-price]');
    const addButton = root.querySelector('[data-add-to-cart]');

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((item) => item.classList.remove('is-active'));
        button.classList.add('is-active');

        variantInput.value = button.dataset.variantId;

        if (priceNode && button.dataset.variantPrice) {
          priceNode.textContent = button.dataset.variantPrice;
        }

        if (comparePriceNode) {
          const hasCompare = button.dataset.variantHasCompare === 'true';
          comparePriceNode.hidden = !hasCompare;
          if (hasCompare && button.dataset.variantComparePrice) {
            comparePriceNode.textContent = button.dataset.variantComparePrice;
          }
        }

        if (addButton) {
          const available = button.dataset.variantAvailable === 'true';
          addButton.disabled = !available;
          addButton.textContent = available
            ? addButton.dataset.addText || 'Add to cart'
            : addButton.dataset.soldOutText || 'Out of stock';
        }
      });
    });
  };

  /* Wheel or trackpad over the track scrolls it sideways instead of the page. */
  const enableWheelHorizontalScroll = (element) => {
    if (!element) return;

    const LINE_HEIGHT = 16;

    element.addEventListener(
      'wheel',
      (event) => {
        if (event.ctrlKey) return; /* pinch zoom */

        const maxScroll = element.scrollWidth - element.clientWidth;
        if (maxScroll <= 1) return;

        const raw = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (!raw) return;

        const delta = event.deltaMode === 1 ? raw * LINE_HEIGHT : raw;
        const target = Math.min(Math.max(element.scrollLeft + delta, 0), maxScroll);

        /* At either end, hand the gesture back so the page keeps scrolling. */
        if (Math.abs(target - element.scrollLeft) < 1) return;

        event.preventDefault();
        element.scrollLeft = target;
      },
      { passive: false }
    );
  };

  const setupThumbnails = (root) => {
    const gallery = root.querySelector('[data-product-gallery]');
    const track = root.querySelector('[data-thumbnails-track]');
    const prev = root.querySelector('[data-thumbnail-prev]');
    const next = root.querySelector('[data-thumbnail-next]');
    if (!gallery || !track) return;

    setupCarousel(track, track, prev, next);
    enableWheelHorizontalScroll(track);

    const items = Array.from(gallery.querySelectorAll('.fallconi-product__gallery-item'));
    const thumbnails = Array.from(track.querySelectorAll('[data-thumbnail]'));
    if (!items.length || !thumbnails.length) return;

    const setActive = (index) => {
      thumbnails.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
    };

    const centerThumbnail = (thumb) => {
      if (!thumb) return;
      const target = thumb.offsetLeft - (track.clientWidth - thumb.clientWidth) / 2;
      track.scrollTo({ left: Math.max(target, 0), behavior: 'smooth' });
    };

    let ticking = false;
    const syncFromScroll = () => {
      const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
      const clamped = Math.min(Math.max(index, 0), items.length - 1);
      setActive(clamped);
      centerThumbnail(thumbnails[clamped]);
    };

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        const item = items[index];
        if (item) gallery.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
        setActive(index);
      });
    });

    gallery.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        syncFromScroll();
        ticking = false;
      });
    });

    window.addEventListener('resize', syncFromScroll);
  };

  const init = (root) => {
    if (!root || root.dataset[INITIALISED] === 'true') return;
    root.dataset[INITIALISED] = 'true';

    setupCarousel(
      root.querySelector('[data-lookbook]'),
      root.querySelector('[data-lookbook-track]'),
      root.querySelector('[data-lookbook-prev]'),
      root.querySelector('[data-lookbook-next]')
    );

    setupThumbnails(root);

    setupVariantPicker(root);
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('[data-product-section-id]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
